import type {HotelDetails} from '@/api/hoteldetails';
import type {Price} from '@/redux/v1/models';
import type {ImageGalleryLayerProps} from '@travel-frontend/ui-components';

import {useBreakpointDown} from '@travel-frontend/core';
import {
  HeadlineLarge,
  ImageGalleryLayer,
  LabelSmall,
} from '@travel-frontend/ui-components';
import {memo, useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {HotelCategory} from '@/components/HotelCategory';
import {PictureGalleryCarouselOverlay} from '@/components/PictureGalleryCarouselOverlay';
import {PictureGalleryMosaicOverlay} from '@/components/PictureGalleryMosaicOverlay';
import {useLocalSplit} from '@/modules/common/hooks';
import {useTracking} from '@/modules/common/hooks/useTracking';

// @FIXME export type in toolkit
type View = 'overview' | 'main';

export type Props = {
  hotelDetails?: HotelDetails;
  hotelImages: ImageGalleryLayerProps['images'];
  isVisible: boolean;
  view?: View;
  selectedImageIndex?: number;
  offerPrice?: Price;
  onClose: () => void;
  onClickCTAButton: () => void;
  onChangeView: (view: View) => void;
};

export const HotelPictureGalleryOverlay: React.FunctionComponent<Props> = memo(
  function HotelPictureGalleryOverlay({
    hotelDetails,
    hotelImages,
    isVisible,
    view = 'main',
    selectedImageIndex,
    offerPrice,
    onClose,
    onClickCTAButton,
    onChangeView,
  }) {
    const {t} = useTranslation('imageGalleryLayer');
    const smDown = useBreakpointDown('sm');

    const newGallerySplit = useLocalSplit('FBO_3197_picturegallery');

    const {dispatchGenericTrackingEvent} = useTracking();

    const handleOnCloseClick = useCallback(() => {
      onClose();
      dispatchGenericTrackingEvent({
        event: 'gallery',
        event_action: 'close_gallery_details',
        non_interaction: false,
      });
    }, [onClose, dispatchGenericTrackingEvent]);

    if (
      !newGallerySplit.ready ||
      hotelDetails === undefined ||
      hotelImages.length === 0
    ) {
      return null;
    }

    if (newGallerySplit.treatment === 'on') {
      return (
        <ImageGalleryLayer
          open={isVisible}
          view={view}
          title={t('title')}
          closeButtonLabel={t('closeButtonLabel')}
          hotel={hotelDetails.name}
          rating={hotelDetails.category ?? 0}
          verified={hotelDetails.categoryType}
          images={hotelImages}
          offerPrice={
            offerPrice && {
              amount: offerPrice.amount / 100, // priceFormatter in toolkit does not accept cents...
              currency: offerPrice.currency,
              locale: import.meta.env.VITE_LOCALE,
            }
          }
          onClose={onClose}
          onOfferButtonClick={onClickCTAButton}
          onViewChange={onChangeView}
        />
      );
    }

    if (smDown) {
      return (
        <PictureGalleryMosaicOverlay
          visible={isVisible}
          images={hotelImages.map((image) => image.image_url)}
          fullScreen
          onClose={onClose}
        />
      );
    }

    return (
      <PictureGalleryCarouselOverlay
        visible={isVisible}
        images={hotelImages.map((image) => ({
          small: {url: image.image_url},
          large: {url: image.image_url},
        }))}
        selectedImageIndex={selectedImageIndex}
        header={
          // @TODO own component?
          <div>
            <HotelCategory
              type={hotelDetails.categoryType}
              value={hotelDetails.category}
            />
            <HeadlineLarge>{hotelDetails.name}</HeadlineLarge>
            <LabelSmall color="contentSecondary">
              {[
                hotelDetails.address.city,
                hotelDetails.address.region,
                hotelDetails.address.country,
              ]
                .filter(Boolean)
                .join(', ')}
            </LabelSmall>
          </div>
        }
        onClose={handleOnCloseClick}
      />
    );
  }
);
